import React, { useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, XIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { pressPages } from '../data/images';
interface PressReaderProps {
  page: number | null;
  onChange: (page: number) => void;
  onClose: () => void;
}
export function PressReader({
  page,
  onChange,
  onClose
}: PressReaderProps) {
  const {
    t
  } = useLanguage();
  const closeRef = useRef<HTMLButtonElement>(null);
  const open = page !== null;
  const total = pressPages.length;
  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement as HTMLElement | null;
    document.body.style.overflow = 'hidden';
    const frame = requestAnimationFrame(() => closeRef.current?.focus());
    return () => {
      cancelAnimationFrame(frame);
      document.body.style.overflow = '';
      previous?.focus?.();
    };
  }, [open]);
  useEffect(() => {
    if (page === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight' && page < total - 1) onChange(page + 1);
      if (e.key === 'ArrowLeft' && page > 0) onChange(page - 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [page, total, onChange, onClose]);
  const navBtn = 'inline-flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white transition-colors duration-150 hover:bg-white/20 disabled:pointer-events-none disabled:opacity-30';
  return <AnimatePresence data-id="element-295" data-mp="1ci6drs6yotc4">
      {page !== null && <motion.div role="dialog" aria-modal="true" aria-label={t.press.headline} initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease: 'easeOut'
    }} className="on-dark fixed inset-0 z-[60] flex flex-col bg-ink/95" data-id="element-296" data-mp="1hfy6upjcelhw">
          <div className="flex items-center justify-between px-5 py-4 text-white md:px-8" data-id="element-297" data-mp="39gvjg08amj9n">
            <p className="text-[14px] text-white/75" data-id="element-298" data-mp="35cxn1xacrmka">
              {t.press.page} {page + 1} {t.press.of} {total}
            </p>
            <button ref={closeRef} type="button" onClick={onClose} aria-label={t.press.close} className={navBtn} data-id="element-299" data-mp="30gqfk02b6308">
              <XIcon className="h-5 w-5" data-id="element-300" data-mp="3t3bdxkh7bfda" />
            </button>
          </div>

          <div className="relative flex min-h-0 flex-1 items-center justify-center px-4 pb-6 md:px-24" data-id="element-301" data-mp="3t5e1lxuvsweu">
            <AnimatePresence mode="wait" initial={false} data-id="element-302" data-mp="3o6ko45flh5eo">
              <motion.img key={page} src={pressPages[page].src} alt={t.press.pageAlts[page]} initial={{
            opacity: 0,
            scale: 0.98
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 0.2,
            ease: [0.23, 1, 0.32, 1]
          }} className="max-h-full max-w-full rounded-md object-contain shadow-2xl" data-id="element-303" data-mp="3jc71osiwu8m5" />
            </AnimatePresence>

            <button type="button" onClick={() => onChange(page - 1)} disabled={page === 0} aria-label={t.press.prev} className={`${navBtn} absolute left-4 top-1/2 hidden -translate-y-1/2 md:inline-flex md:left-8`} data-id="element-304" data-mp="n8z6bggo185h">
              <ChevronLeftIcon className="h-5 w-5" data-id="element-305" data-mp="116nrkpenahb2" />
            </button>
            <button type="button" onClick={() => onChange(page + 1)} disabled={page === total - 1} aria-label={t.press.next} className={`${navBtn} absolute right-4 top-1/2 hidden -translate-y-1/2 md:inline-flex md:right-8`} data-id="element-306" data-mp="166iigsyf76w4">
              <ChevronRightIcon className="h-5 w-5" data-id="element-307" data-mp="1kw7vblfvkzc8" />
            </button>
          </div>

          <div className="flex items-center justify-center gap-4 pb-6 md:hidden" data-id="element-308" data-mp="1ox05qi0hhaa3">
            <button type="button" onClick={() => onChange(page - 1)} disabled={page === 0} aria-label={t.press.prev} className={navBtn} data-id="element-309" data-mp="2mm65z1netbp0">
              <ChevronLeftIcon className="h-5 w-5" data-id="element-310" data-mp="2hm0a19a3x314" />
            </button>
            <button type="button" onClick={() => onChange(page + 1)} disabled={page === total - 1} aria-label={t.press.next} className={navBtn} data-id="element-311" data-mp="2ve82ioucy7u0">
              <ChevronRightIcon className="h-5 w-5" data-id="element-312" data-mp="2rdegk10h733r" />
            </button>
          </div>
        </motion.div>}
    </AnimatePresence>;
}